/*******************************************************
 * Copyright (C) BoostCommerce.net
 *
 * This file is part of commercial BoostCommerce.net projects.
 *
 * This file can not be copied and/or distributed without the express
 * permission of BoostCommerce.net
 *
 * @Date:   2023-01-11 14:21:40
 *
 *******************************************************/
// @flow
import _ from 'lodash';
import classnames from 'classnames';
import React, { Component } from 'react';
import { FormLayout, Icon, Link } from '@shopify/polaris';
import { CaretDownMinor } from '@shopify/polaris-icons';
import { Field } from 'redux-form';
import { withI18n, WithI18nProps } from '@shopify/react-i18n';
import { RenderRadioButtonItem } from 'src/components/views/common/forms/render-radio-button-item';
import { RenderToggleSwitch } from 'src/components/views/common/forms/render-toggle-switch';
import { RenderTooltip } from 'src/components/views/common/render-tooltip';
import { RenderTextField } from 'src/components/views/common/forms/render-textfield';
import RenderColorPickerItem from 'src/components/views/common/render-color-picker-item';
import { Collapse } from 'react-collapse';
import { RenderRadioButton2 } from 'src/components/views/common/forms/render-radio-button-2';
import { DropdownIndicator, styles } from './react-select-style';
import { RenderSelect2 } from 'src/components/views/common/forms/render-select-2-v2';
import {
  DEFAULT_FONT_SIZE,
  TEXT_TRANSFORM_OPTIONS,
  OVERALL_BUTTON_SHAPE_OPTIONS
} from 'src/constants/theme-settings';
import { AJAX_CART_DISPLAY_TYPES } from 'src/constants/theme-settings.jsx';
import TextForm from './text-form';
import { nonXSSv2 } from 'src/helpers/form-validation-helper';
import { checkHasXss } from 'src/helpers';
import { URL } from 'src/constants/links';

type Props = {
  formValues: any,
  updateField: (field: string, value: any) => void
};

type ComposedProps = Props & WithI18nProps;

const nonXSSValue = nonXSSv2();

class AjaxCartForm extends Component<ComposedProps> {
  state = {
    isOpenedCheckout: true,
    isOpenedViewCart: true,
    isOpenedGeneralLayout: true
  };

  /**
   * Handle toggle collapse by field
   * @param {*} field
   * @returns
   */
  handleToggleCollapse = field => {
    this.setState({ [field]: !this.state[field] });
  };

  /**
   * Build color pickers from form values redux
   * @param {*} formValues
   * @returns
   */
  getColorPickers = prefix => {
    const { i18n, formValues } = this.props;
    return [
      {
        colorValue: _.get(formValues, `${prefix}.color`),
        fieldName: `${prefix}.color`,
        fieldLabel: i18n.translate('AdminHTML.ThemeSetting.Form.quickView_AddToCart_Color_Item1')
      },
      {
        colorValue: _.get(formValues, `${prefix}.hoverColor`),
        fieldName: `${prefix}.hoverColor`,
        fieldLabel: i18n.translate('AdminHTML.ThemeSetting.Form.quickView_AddToCart_Color_Item2')
      },
      {
        colorValue: _.get(formValues, `${prefix}.backgroundColor`),
        fieldName: `${prefix}.backgroundColor`,
        fieldLabel: i18n.translate('AdminHTML.ThemeSetting.Form.quickView_AddToCart_Color_Item3')
      },
      {
        colorValue: _.get(formValues, `${prefix}.hoverBackgroundColor`),
        fieldName: `${prefix}.hoverBackgroundColor`,
        fieldLabel: i18n.translate('AdminHTML.ThemeSetting.Form.quickView_AddToCart_Color_Item4')
      },
      {
        colorValue: _.get(formValues, `${prefix}.borderColor`),
        fieldName: `${prefix}.borderColor`,
        fieldLabel: i18n.translate('AdminHTML.ThemeSetting.Form.quickView_AddToCart_Color_Item5')
      },
      {
        colorValue: _.get(formValues, `${prefix}.hoverBorderColor`),
        fieldName: `${prefix}.hoverBorderColor`,
        fieldLabel: i18n.translate('AdminHTML.ThemeSetting.Form.quickView_AddToCart_Color_Item6')
      }
    ];
  };

  /**
   * Get AJAX cart display types
   * @returns
   */
  getDisplayTypes = () => {
    const { i18n, prefix } = this.props;

    return AJAX_CART_DISPLAY_TYPES.map((option, index) => {
      const { label, value, tooltipImage, tooltipText } = option;
      return (
        <div key={index} style={{ display: 'flex', gap: '6px' }}>
          <Field
            name={`${prefix}.cartStyle`}
            id={value}
            component={RenderRadioButtonItem}
            label={i18n.translate(label)}
          />
          {tooltipImage && (
            <RenderTooltip content={<img src={tooltipImage} alt="" />} icon={true} />
          )}
          {tooltipText && <RenderTooltip content={i18n.translate(tooltipText)} icon={true} />}
        </div>
      );
    });
  };

  /**
   * Build select option
   * @param {*} options
   * @returns
   */
  buildSelectOptions = options => {
    const { i18n } = this.props;
    return options.map(option => {
      const { label, value } = option;

      return { label: i18n.translate(label), value };
    });
  };

  /**
   * Blur text field
   * @param {*} event
   * @param {*} value
   * @param {*} name
   */
  onBlurTextField = (event, value, name) => {
    const { updateField } = this.props;
    if (!value) {
      event.preventDefault();
      updateField(name, DEFAULT_FONT_SIZE);
    }
  };

  /**
   * Render text transform
   */
  getTextTransform = (prefix: string) => {
    const { i18n } = this.props;

    return (
      <div className="form-section" style={{ marginTop: '12px' }}>
        <p className="title title--small">
          {i18n.translate('AdminHTML.ThemeSetting.Form.text_Title')}
        </p>
        <p className="subtitle">
          {i18n.translate('AdminHTML.ThemeSetting.Form.textTransform_Title')}
        </p>
        <div className="form-section__item">
          <div className="radio-button-wrapper">
            {TEXT_TRANSFORM_OPTIONS.map((item, index) => (
              <Field
                key={index}
                name={`${prefix}.textTransform`}
                id={item.value}
                component={RenderRadioButton2}
                label={item.label}
              />
            ))}
          </div>
        </div>
      </div>
    );
  };

  handleUnmountInput = fieldName => {
    const { formValues, updateField } = this.props;
    const text = _.get(formValues, fieldName, '');
    if (checkHasXss(text)) {
      updateField(fieldName, '');
    }
  };

  render() {
    const { i18n, formValues, prefix, labelTranslation, updateField } = this.props;
    const { isOpenedCheckout, isOpenedViewCart, isOpenedGeneralLayout } = this.state;

    const displayNone = _.get(formValues, `${prefix}.cartStyle`) !== 'none';
    const ajaxCartEnabled = _.get(formValues, `${prefix}.enableCart`);
    const showSettings = ajaxCartEnabled && displayNone;

    return (
      <FormLayout>
        <div>
          <div className="form-section" style={{ marginBottom: '20px' }}>
            <p className="description-section">
              {i18n.translate('AdminHTML.ThemeSetting.Form.ajaxCart_HelpText_Label', {
                t1: (
                  <Link external url={URL['AjaxCartThemeSetting']}>
                    {i18n.translate('AdminHTML.ThemeSetting.Form.quickView_HelpText_t1')}
                  </Link>
                )
              })}
            </p>
            <div className="form-section__item form-section__item--medium-spacing">
              <Field
                name={`${prefix}.enableCart`}
                component={RenderToggleSwitch}
                label={i18n.translate('AdminHTML.ThemeSetting.Form.ajaxCart_Toggle_Label')}
              />
            </div>
            {ajaxCartEnabled && (
              <div
                className="form-section__item form-section__item--medium-spacing"
                style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                <p>{i18n.translate('AdminHTML.ThemeSetting.Form.ajaxCart_DisplayType_Label')}</p>
                {this.getDisplayTypes()}
              </div>
            )}
          </div>

          {/* GENERAL LAYOUT */}
          {showSettings && (
            <div className="form-section form-section--bordered">
              <div
                className="section-title-collapse__wrapper"
                onClick={() => this.handleToggleCollapse('isOpenedGeneralLayout')}>
                <p className="section-title">
                  {i18n.translate('AdminHTML.ThemeSetting.Form.ajaxCart_GeneralLayout_Header')}
                </p>
                <span className={classnames('caret', { collapsed: !isOpenedGeneralLayout })}>
                  <Icon source={CaretDownMinor} color="base" />
                </span>
              </div>
              <Collapse isOpened={isOpenedGeneralLayout}>
                <div className="form-section">
                  <div className="form-section__item">
                    <Field
                      name={`${prefix}.generalLayout.shape`}
                      label={i18n.translate(
                        'AdminHTML.ThemeSetting.Form.button_Overall_Shape_Item'
                      )}
                      options={this.buildSelectOptions(OVERALL_BUTTON_SHAPE_OPTIONS)}
                      component={RenderSelect2}
                      components={{ DropdownIndicator }}
                      styles={styles}
                      isSearchable={false}
                    />
                  </div>
                </div>
                <TextForm
                  formValues={formValues}
                  updateField={updateField}
                  prefix={`${prefix}.generalLayout`}
                />
              </Collapse>
            </div>
          )}

          {/* VIEW CART BUTTON */}
          {showSettings && (
            <div className="form-section form-section--bordered">
              <div
                className="section-title-collapse__wrapper"
                onClick={() => this.handleToggleCollapse('isOpenedViewCart')}>
                <p className="section-title">
                  {i18n.translate('AdminHTML.ThemeSetting.Form.ajaxCart_ViewCartButton_Header')}
                </p>
                <span className={classnames('caret', { collapsed: !isOpenedViewCart })}>
                  <Icon source={CaretDownMinor} color="base" />
                </span>
              </div>
              <Collapse isOpened={isOpenedViewCart}>
                <div className="form-section">
                  <div className="form-section__item">
                    <Field
                      name={`${prefix}.viewCartBtn.text`}
                      component={RenderTextField}
                      label={i18n.translate(
                        'AdminHTML.ThemeSetting.Form.ajaxCart_ViewCartButton_ButtonText_Label'
                      )}
                      placeholder={
                        labelTranslation?.atcMiniCartViewCartLabelV2?.value ||
                        i18n.translate(
                          'AdminHTML.ThemeSetting.Form.ajaxCart_ViewCartButton_ButtonText_Placeholder'
                        )
                      }
                      validate={[nonXSSValue]}
                      onUnmount={() => this.handleUnmountInput(`${prefix}.viewCartBtn.text`)}
                    />
                  </div>
                  <div className="form-section" style={{ marginTop: '12px' }}>
                    <p className="title title-small">
                      {i18n.translate('AdminHTML.ThemeSetting.Form.color_Title')}
                    </p>
                    {this.getColorPickers(`${prefix}.viewCartBtn`).map((item, index) => (
                      <RenderColorPickerItem
                        key={index}
                        colorValue={item.colorValue}
                        fieldName={item.fieldName}
                        fieldLabel={item.fieldLabel}
                        updateField={updateField}
                      />
                    ))}
                  </div>
                  {this.getTextTransform(`${prefix}.viewCartBtn`)}
                </div>
              </Collapse>
            </div>
          )}

          {/* CHECKOUT BUTTON */}
          {showSettings && (
            <div className="form-section form-section--bordered">
              <div
                className="section-title-collapse__wrapper"
                onClick={() => this.handleToggleCollapse('isOpenedCheckout')}>
                <p className="section-title">
                  {i18n.translate('AdminHTML.ThemeSetting.Form.ajaxCart_CheckoutButton_Header')}
                </p>
                <span className={classnames('caret', { collapsed: !isOpenedCheckout })}>
                  <Icon source={CaretDownMinor} color="base" />
                </span>
              </div>
              <Collapse isOpened={isOpenedCheckout}>
                <div className="form-section">
                  <div className="form-section__item">
                    <Field
                      name={`${prefix}.checkoutBtn.text`}
                      component={RenderTextField}
                      label={i18n.translate(
                        'AdminHTML.ThemeSetting.Form.ajaxCart_CheckoutButton_ButtonText_Label'
                      )}
                      placeholder={
                        labelTranslation?.atcMiniCartCheckoutLabelV2?.value ||
                        i18n.translate(
                          'AdminHTML.ThemeSetting.Form.ajaxCart_CheckoutButton_ButtonText_Placeholder'
                        )
                      }
                      validate={[nonXSSValue]}
                      onUnmount={() => this.handleUnmountInput(`${prefix}.checkoutBtn.text`)}
                    />
                  </div>
                  <div className="form-section" style={{ marginTop: '12px' }}>
                    <p className="title title-small">
                      {i18n.translate('AdminHTML.ThemeSetting.Form.color_Title')}
                    </p>
                    {this.getColorPickers(`${prefix}.checkoutBtn`).map((item, index) => (
                      <RenderColorPickerItem
                        key={index}
                        colorValue={item.colorValue}
                        fieldName={item.fieldName}
                        fieldLabel={item.fieldLabel}
                        updateField={updateField}
                      />
                    ))}
                  </div>
                  {this.getTextTransform(`${prefix}.checkoutBtn`)}
                </div>
              </Collapse>
            </div>
          )}
        </div>
      </FormLayout>
    );
  }
}

export default withI18n()(AjaxCartForm);
